import axios from 'axios';
import {
  ReactNode,
  createContext, useEffect, useMemo,
  useState,
} from 'react';
import { useLogger } from 'react-use';

import { truefileServerApi } from 'api/truefilesServerApi';
import { IUserModel } from 'api/types/trueFileServerTypes';
import { AsyncWrapper } from 'components/AsyncWrapper';

export interface IUserState {
  user: IUserModel | undefined;
  isLoggedIn: boolean;
  login: (email: string, password: string)=> Promise<void>;
  logout: ()=> Promise<void>;
  refreshUser: ()=> Promise<void>;
}

export const UserContext = createContext<IUserState>({
  user: undefined,
  isLoggedIn: false,
  login: async () => { throw new Error('login not implemented'); },
  logout: async () => { throw new Error('logout not implemented'); },
  refreshUser: async () => { throw new Error('refreshUser not implemented'); },
});

async function logIn(email: string, password: string): Promise<void> {
  await truefileServerApi.POST('/api/auth/login', {
    body: { email, password },
  });
}

async function getCurrentUser(): Promise<IUserModel | undefined> {
  const resp = await truefileServerApi.GET('/api/auth/me');
  return resp.data;
}

async function logOut(params?: URLSearchParams | string): Promise<void> {
  console.warn('sign out flow triggered...');
  await truefileServerApi.POST('/api/auth/logout');
  localStorage.clear();
  const urlParams = params ? `?${new URLSearchParams(params).toString()}` : '';
  window.location.href = `/login${urlParams}`;
}

export default function UserContextProvider({ children }: { children: ReactNode }): JSX.Element {
  const [userInfo, setUserInfo] = useState<IUserModel>();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    void getCurrentUser().then((user) => {
      setUserInfo(user);
      setIsLoading(false);
    }).catch((e: any) => {
      setIsLoading(false);
      throw new Error(e);
    });
  }, []);

  const state: IUserState = useMemo(() => ({
    user: userInfo,
    isLoggedIn: !!userInfo,
    login: async (email: string, password: string) => {
      setIsLoading(true);
      try {
        await logIn(email, password);
        const user = await getCurrentUser();
        setUserInfo(user);
      } catch (e: any) {
        throw new Error(e);
      } finally {
        setIsLoading(false);
      }
    },
    logout: async () => {
      setIsLoading(true);
      try {
        await logOut();
      } catch (e: any) {
        throw new Error(e);
      } finally {
        setIsLoading(false);
      }
    },
    refreshUser: async () => {
      setIsLoading(true);
      try {
        const user = await getCurrentUser();
        setUserInfo(user);
      } catch (e: any) {
        throw new Error(e);
      } finally {
        setIsLoading(false);
      }
    },
  }), [userInfo]);

  useLogger('userContext');

  return (
    <UserContext.Provider value={state}>
      <AsyncWrapper requests={[{ loading: isLoading }]}>
        {children}
      </AsyncWrapper>
    </UserContext.Provider>
  );
}
